import { useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from '../api/axios';
import { isAxiosError } from 'axios';

type LoginForm = {
  email: string;
  password: string;
};

interface LoginModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export default function LoginModal({ onClose, onSuccess }: LoginModalProps) {
  const closeButtonClassName =
    'absolute top-[8px] right-[8px] z-[2147483647] inline-flex h-6 w-6 items-center justify-center rounded-full border text-white';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (data: LoginForm) => {
    setError(null);
    setIsLoading(true);

    try {
      const res = await axios.post('/auth/login', data);

      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        if (res.data.refreshToken) {
          localStorage.setItem('refreshToken', res.data.refreshToken);
        }

        onSuccess?.();
      } else {
        setError('Nieprawidłowa odpowiedź z serwera');
      }
    } catch (e) {
      setError(isAxiosError(e) ? e.response?.data?.error ?? 'Wystąpił błąd podczas logowania' : 'Wystąpił błąd podczas logowania');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed top-0 left-0 z-[2147483646] w-screen h-screen flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/35" onClick={onClose} aria-hidden="true" />

      <div className="relative w-[min(92vw,28rem)]">
        <button
          id="close-button"
          type="button"
          onClick={onClose}
          className={`modal-close-btn ${closeButtonClassName}`}
          aria-label="Zamknij"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>

        <div
          id="login-popup"
          className="relative glass-panel-strong sidebar-glass bg-[rgba(11,1,54,0.24)]"
        >
          <div id="login-modal-content" className="m-[24px]">
            <h2 id="login-modal-title" className="text-center text-3xl font-extrabold text-[#ffffff]">Zaloguj się</h2>

            <form id="login-form" className="mt-[10px]" onSubmit={handleSubmit(onSubmit)}>
              <div id="login-form-fields" className="flex flex-col gap-[12px] rounded-md shadow-sm">
                <div id="email-field">
                  <label className="modal-label">Adres email:</label>
                  <input
                    {...register('email', {
                      required: 'Email jest wymagany',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Nieprawidłowy adres email',
                      },
                    })}
                    type="email"
                    className="modal-input"
                    placeholder="Adres email"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                </div>
                <div id="password-field">
                  <label className="modal-label">Hasło:</label>
                  <input
                    {...register('password', {
                      required: 'Hasło jest wymagane',
                      minLength: {
                        value: 6,
                        message: 'Hasło musi mieć co najmniej 6 znaków',
                      },
                    })}
                    type="password"
                    className="modal-input"
                    placeholder="Hasło"
                  />
                  {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
                </div>
              </div>

              {error && (
                <div className="mt-[18px] rounded-md bg-red-50/90 border border-red-200 p-4">
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              )}

              <button
                id="submit-button"
                type="submit"
                disabled={isLoading}
                className="btn-action btn-green btn-full"
              >
                {isLoading ? 'Logowanie...' : 'Zaloguj się'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
